import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { useFinanceProductionByProcedure } from '@hooks/dashboard/useFinanceProductionByProcedure'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  financeTrendLine,
  formatProductionThousands,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FinanceMetricTilesRow,
  FinanceProductionByProcedureHeader,
} from '@components/pages/Dashboard/Finance/FinanceMetricTiles.component'
import {
  FINANCE_PRODUCTION_BY_PROCEDURE_BAR_CATEGORY_GAP,
  FINANCE_PRODUCTION_BY_PROCEDURE_BAR_SIZE,
} from '@constants/Dashboard/financeDashboard.constants'

type ProductionByProcedureCardPanel = {
  prodGoalLabel: string
  changeSignedPct: string
  changeAmt: string
  gross: string
  net: string
  adjustment: string
  /** Raw numbers backing the formatted strings above (used for emptiness). */
  grossValue: number
  netValue: number
  adjustmentValue: number
  /** Production summary response has arrived (`data != null`). */
  hasPayload: boolean
}

type ProductionByProcedureCardProps = {
  titleHref: string
  range: RangeKey
  prodByProcedure: ReturnType<typeof useFinanceProductionByProcedure>
  productionPanel: ProductionByProcedureCardPanel
}

export function ProductionByProcedureCard({
  titleHref,
  range,
  prodByProcedure,
  productionPanel,
}: ProductionByProcedureCardProps): React.ReactElement {
  const {
    loading,
    chartConfig,
    categoryColors,
    hasData: hasChartData,
    hasPayload: hasChartPayload,
  } = prodByProcedure
  const placeholder = HYGIENE_PLACEHOLDER
  // KPI tiles (gross / net / adjustment) and the by-procedure chart come from
  // different responses, so each can be empty independently.
  const hasProductionKpis = hasNonZeroDashboardValues(
    productionPanel.grossValue,
    productionPanel.netValue,
    productionPanel.adjustmentValue,
  )
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasChartData || hasProductionKpis,
  )
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasChartData)
  const hasPayload = hasChartPayload || productionPanel.hasPayload
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Production by procedure"
      titleHref={titleHref}
      className="dashboard-finance-production-procedure-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      headerRight={
        isEmpty ? undefined : (
          <FinanceProductionByProcedureHeader
            trendLine={
              loading
                ? undefined
                : financeTrendLine(
                    range,
                    productionPanel.changeSignedPct,
                    productionPanel.changeAmt,
                  )
            }
            goalLabel={loading ? '' : productionPanel.prodGoalLabel}
          />
        )
      }
    >
      <FinanceMetricTilesRow
        columns={4}
        metrics={[
          {
            id: 'gross-prod',
            leftLabel: 'Gross production',
            value: loading ? placeholder : productionPanel.gross,
          },
          {
            id: 'net-prod',
            leftLabel: 'Net production',
            value: loading ? placeholder : productionPanel.net,
          },
          {
            id: 'goal',
            leftLabel: 'Goal',
            value: loading ? placeholder : productionPanel.prodGoalLabel,
          },
          {
            id: 'adj-ratio',
            leftLabel: 'Adj. Production Ratio',
            value: loading ? placeholder : productionPanel.adjustment,
          },
        ]}
      />
      {isChartEmpty ? (
        <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
      ) : (
        <div className="dashboard-finance-production-procedure-chart-wrap">
          <DashboardStackedBarChart
            data={loading ? [] : chartConfig.data}
            series={[{ dataKey: 'value', name: '', color: '' }]}
            horizontal={false}
            stacked={false}
            categoryIndicatorColors={categoryColors}
            hideCategoryIndicatorCircles
            hideCategoryAxisLabels
            valueFormatter={formatProductionThousands}
            height={302}
            showLegend={false}
            yAxisDomain={chartConfig.yDomain}
            yAxisTicks={chartConfig.yTicks}
            margin={{ left: 0, right: 8, bottom: 12 }}
            barSize={FINANCE_PRODUCTION_BY_PROCEDURE_BAR_SIZE}
            barCategoryGap={FINANCE_PRODUCTION_BY_PROCEDURE_BAR_CATEGORY_GAP}
            className="dashboard-finance-production-by-procedure-chart"
          />
          <div className="dashboard-finance-production-procedure-legend">
            {Object.keys(categoryColors).map((label) => (
              <span
                key={label}
                className="dashboard-finance-production-procedure-legend-item"
              >
                <span
                  className="dashboard-finance-production-procedure-legend-dot"
                  style={{ background: categoryColors[label] }}
                />
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </DashboardSectionCard>
  )
}
